/**
 * Kono operators: the quote email, editable before it goes (To, visible
 * copies, subject, body; the PDF is always attached), and under it every
 * email around this quote and the RFQ it answers (what we sent, what the
 * customer wrote back, what was held and never sent), oldest first.
 */
import { useCallback, useEffect, useState } from "react";
import {
  emailQuote,
  loadQuoteEmailDraft,
  loadQuoteEmailThread,
  type Quote,
  type QuoteEmailDraft,
  type QuoteEmailThread,
  type QuoteEmailThreadEntry,
} from "./api";

const messageOf = (e: unknown): string => (e instanceof Error ? e.message : String(e));

const when = (iso: string): string => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const SOURCE_LABEL: Record<QuoteEmailThreadEntry["source"], string> = {
  "rfq-email": "RFQ email",
  "quote-email": "Quote email",
  "quote-share": "Quote shared",
  mailbox: "Mailbox",
  held: "Held, not sent",
};

const EntryRow = ({ e }: { e: QuoteEmailThreadEntry }) => {
  const [open, setOpen] = useState(false);
  const inbound = e.direction === "in";
  return (
    <div className={`rounded-lg border px-3 py-2 text-sm ${inbound ? "border-sky-200 bg-sky-50" : e.held ? "border-dashed border-gray-300 bg-gray-50" : "border-gray-200 bg-white"}`}>
      <button type="button" className="flex w-full flex-wrap items-center gap-x-2 gap-y-1 text-left" onClick={() => setOpen((v) => !v)}>
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${inbound ? "bg-sky-200 text-sky-900" : e.held ? "bg-gray-200 text-gray-700" : "bg-emerald-100 text-emerald-900"}`}>
          {inbound ? "Customer" : e.held ? "Held" : "Kono"}
        </span>
        <span className="text-xs text-gray-500">{when(e.at)}</span>
        <span className="font-medium text-gray-900">{e.subject || "(no subject)"}</span>
        <span className="text-xs text-gray-500">
          {inbound ? `from ${e.from || "the customer"}` : `to ${e.to.join(", ") || "—"}`}
          {e.cc.length ? ` · cc ${e.cc.join(", ")}` : ""}
          {e.quoteReference ? ` · ${e.quoteReference}` : ""}
          {e.by && !inbound ? ` · by ${e.by}` : ""}
          {` · ${SOURCE_LABEL[e.source] || e.kind}`}
        </span>
        {e.tracking && (
          <span className="text-xs text-gray-500" title="Delivery and reads, as far as tracking can tell">
            {e.tracking.bounced ? "bounced" : e.tracking.delivered ? "delivered" : "sent"}
            {e.tracking.opens ? ` · opened ${e.tracking.opens}x` : ""}
            {e.tracking.clicks ? ` · clicked ${e.tracking.clicks}x` : ""}
          </span>
        )}
        <span className="ml-auto text-xs text-gray-400">{open ? "hide" : e.text ? "show" : ""}</span>
      </button>
      {open && (
        <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap rounded bg-white p-2 font-sans text-xs text-gray-800">
          {e.text || (inbound ? "Body not kept: this email was seen in the mailbox, open it there for the full text." : "Body not kept.")}
        </pre>
      )}
    </div>
  );
};

export function OperatorEmailPanel({
  quote,
  composeOpen,
  onClose,
  onSent,
}: {
  quote: Quote;
  /** The editor is open (the operator pressed "Email it"); the thread shows either way. */
  composeOpen: boolean;
  onClose: () => void;
  onSent: (to: string) => void;
}) {
  const [draft, setDraft] = useState<QuoteEmailDraft | null>(null);
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [thread, setThread] = useState<QuoteEmailThread | null>(null);
  const [threadError, setThreadError] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showThread, setShowThread] = useState(true);

  const loadThread = useCallback(async () => {
    try {
      setThread(await loadQuoteEmailThread(quote.id));
      setThreadError("");
    } catch (e) {
      setThreadError(messageOf(e));
    }
  }, [quote.id]);

  useEffect(() => {
    void loadThread();
  }, [loadThread]);

  useEffect(() => {
    if (!composeOpen) return;
    let cancelled = false;
    setError("");
    loadQuoteEmailDraft(quote.id)
      .then((d) => {
        if (cancelled) return;
        setDraft(d);
        setTo(d.to);
        setCc(d.cc.join(", "));
        setSubject(d.subject);
        setText(d.text);
      })
      .catch((e) => !cancelled && setError(messageOf(e)));
    return () => {
      cancelled = true;
    };
  }, [composeOpen, quote.id]);

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const ccList = cc
        .split(/[,;\s]+/)
        .map((x) => x.trim())
        .filter(Boolean);
      await emailQuote(quote.id, to.trim(), { subject: subject.trim(), text, cc: ccList });
      onSent(to.trim());
      void loadThread();
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const edited = draft ? subject !== draft.subject || text !== draft.text : false;
  const entries = thread?.entries ?? [];

  return (
    <div id="operator-email-panel" className="mt-4 rounded-xl border border-gray-200 p-4">
      {composeOpen && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h3 className="text-sm font-semibold text-gray-900">Email this quote</h3>
            <span className="text-xs text-gray-500">
              Edit anything before it goes. The PDF of {quote.reference} is attached; you and whoever prepared the quote are blind-copied
              {draft?.copyTo.length ? `, and ${draft.copyTo.join(", ")} get a copy` : ""}.
            </span>
          </div>
          {!draft && !error && <p className="text-sm text-gray-500">Preparing the email...</p>}
          {draft && (
            <div className="space-y-2">
              <label className="block text-xs text-gray-600">
                To
                <input className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
              </label>
              <label className="block text-xs text-gray-600">
                Cc (visible copies, comma separated)
                <input className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm" value={cc} onChange={(e) => setCc(e.target.value)} placeholder="buyer2@customer.com" />
              </label>
              <label className="block text-xs text-gray-600">
                Subject
                <input className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 text-sm" value={subject} onChange={(e) => setSubject(e.target.value)} />
              </label>
              <label className="block text-xs text-gray-600">
                Body
                <textarea className="mt-0.5 w-full rounded border border-gray-300 px-2 py-1 font-mono text-xs" rows={Math.min(24, Math.max(10, text.split("\n").length + 2))} value={text} onChange={(e) => setText(e.target.value)} />
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className="rounded-full bg-[#00062e] px-4 py-2 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50" onClick={() => void send()} disabled={busy || !to.trim() || !text.trim()}>
                  {busy ? "Sending..." : `Send to ${to.trim() || "..."}`}
                </button>
                {edited && (
                  <button
                    type="button"
                    className="rounded-full border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50"
                    onClick={() => {
                      setSubject(draft.subject);
                      setText(draft.text);
                    }}
                  >
                    Back to the standard email
                  </button>
                )}
                <button type="button" className="rounded-full border border-gray-300 px-3 py-1.5 text-xs hover:bg-gray-50" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                {edited && <span className="text-xs text-amber-800">Edited: this wording goes out, not the standard email.</span>}
              </div>
            </div>
          )}
          {error && <p className="mt-2 text-sm text-red-700">{error}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-900">
          Emails with the customer{entries.length ? ` (${entries.length})` : ""}
        </h3>
        <span className="text-xs text-gray-500">
          Every email around {thread?.quoteReferences.length ? thread.quoteReferences.join(", ") : quote.reference}
          {thread?.rfqId ? " and its RFQ" : ""}: what Kono sent, what the customer wrote back, what was held. Oldest first.
        </span>
        <button type="button" className="ml-auto text-xs text-gray-500 underline" onClick={() => setShowThread((v) => !v)}>
          {showThread ? "hide" : "show"}
        </button>
        <button type="button" className="text-xs text-gray-500 underline" onClick={() => void loadThread()}>
          refresh
        </button>
      </div>
      {threadError && <p className="mt-2 text-sm text-red-700">{threadError}</p>}
      {showThread && thread && entries.length === 0 && <p className="mt-2 text-sm text-gray-500">No email recorded yet for this quote.</p>}
      {showThread && entries.length > 0 && (
        <div className="mt-2 space-y-2">
          {entries.map((e, i) => (
            <EntryRow key={`${e.at}-${i}`} e={e} />
          ))}
        </div>
      )}
    </div>
  );
}
